/**
 * Single accessor for the auth token keys, pinned per-tab via `sessionStorage`.
 *
 * The auth store (`stores/auth.ts`) and the fetch wrapper (`lib/api.ts`)
 * both touch these keys directly. Routing access through this helper
 * prevents a logout flow from leaving an orphaned token behind.
 *
 * Per-tab session pinning (see `docs/plans/per-tab-auth-session-isolation.md`):
 * reads are `sessionStorage`-primary, bootstrapping once from `localStorage`
 * so a brand-new tab still inherits whichever session is currently active
 * (no forced re-login). Writes mirror to both stores — `sessionStorage` so
 * this tab's own view of "who am I" never gets silently overwritten by
 * another tab, and `localStorage` so a *future* new tab still has something
 * to bootstrap from.
 *
 * Persistence mode ("Keep me signed in"): `set()` honours a per-tab mode. In
 * `session` mode it writes `sessionStorage` only AND actively removes the
 * `localStorage` mirror. The removal is the point, not a tidy-up — a mirror
 * left behind by an earlier *remembered* login on this browser would be
 * bootstrapped by the next new tab (see `get()` above) and silently resurrect
 * the very session the user asked not to persist.
 *
 * The mode itself lives in `sessionStorage`, never `localStorage`: a flag whose
 * whole meaning is "this session must not outlive the browser" would be
 * self-defeating if it outlived the browser. Keeping it here also means the
 * 401-refresh write path in `lib/api.ts` and the Zustand adapter in
 * `stores/auth.ts` need no changes at their call sites — `set()` reads the mode
 * internally, so a refreshed token cannot quietly re-persist an un-remembered
 * session on its first refresh.
 *
 * Absent means `persistent`. That is both the pre-existing behaviour and the
 * right default for a new tab bootstrapping from `localStorage`, since those
 * tokens were by definition persisted by a remembered login.
 */

export const TOKEN_KEYS = [
  'access_token',
  'refresh_token',
  'user',
  'org',
  'workspace',
] as const;

export type TokenKey = (typeof TOKEN_KEYS)[number];

/** Whether tokens written by this tab may outlive it. */
export type AuthPersistence = 'persistent' | 'session';

/** Deliberately not a `TokenKey` — it describes the keys, it isn't one of them. */
const PERSISTENCE_KEY = 'auth_persistence';

const isBrowser = (): boolean => typeof window !== 'undefined';

const isSessionOnly = (): boolean =>
  sessionStorage.getItem(PERSISTENCE_KEY) === 'session';

export const tokenStorage = {
  get(key: TokenKey): string | null {
    if (!isBrowser()) return null;
    const tabValue = sessionStorage.getItem(key);
    if (tabValue !== null) return tabValue;
    const bootstrapped = localStorage.getItem(key);
    if (bootstrapped !== null) sessionStorage.setItem(key, bootstrapped);
    return bootstrapped;
  },
  /**
   * Records whether later writes may touch `localStorage`. Call this *before*
   * the first token write of a session. `persistent` is stored as the absence
   * of the flag so there is exactly one representation of the default state.
   */
  setPersistence(mode: AuthPersistence): void {
    if (!isBrowser()) return;
    if (mode === 'session') sessionStorage.setItem(PERSISTENCE_KEY, mode);
    else sessionStorage.removeItem(PERSISTENCE_KEY);
  },
  /**
   * The choice this tab is currently signed in under. Read it instead of
   * touching `PERSISTENCE_KEY` from outside — the re-auth modal uses this to
   * carry the user's original "Keep me signed in" answer into the new session,
   * since a session expiring is not the user changing their mind.
   */
  getPersistence(): AuthPersistence {
    if (!isBrowser()) return 'persistent';
    return isSessionOnly() ? 'session' : 'persistent';
  },
  set(key: TokenKey, value: string): void {
    if (!isBrowser()) return;
    sessionStorage.setItem(key, value);
    if (isSessionOnly()) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, value);
  },
  remove(key: TokenKey): void {
    if (!isBrowser()) return;
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  },
  clearAll(): void {
    if (!isBrowser()) return;
    for (const key of TOKEN_KEYS) {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    }
    // Reset to the persistent default on logout. The mode describes one
    // session; leaving it set would silently make the *next* sign-in in this
    // tab session-only too — including OAuth sign-ins, which never express a
    // "Keep me signed in" preference of their own.
    sessionStorage.removeItem(PERSISTENCE_KEY);
  },
};
